
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getClientResponse } from '@/services/client-cache-service';
import { TopicType } from '@/types/assistant';

// Common questions for prefetching by topic
const COMMON_QUESTIONS: Record<TopicType, string[]> = {
  guide: ['How do I get started?', 'What are the main features?', 'How can I customize my settings?'],
  performance: ['How to analyze performance?', 'What metrics should I track?', 'How to improve my strategy?'],
  trading_strategy: ['What is a good trading strategy?', 'How to backtest my strategy?', 'What are common mistakes?'],
  advisor: ['How to manage risk?', 'What allocation is recommended?', 'How to diversify my portfolio?'],
};

export function usePrefetchQuestions(
  currentSessionId: string | null,
  currentTopic: TopicType | null,
  isOnline: boolean,
  language: string
) {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    // Early return if required values are null/undefined
    if (!currentSessionId || !currentTopic || !isOnline) {
      return;
    }
    
    // Prefetch common questions for the current topic
    const questionsToPreload = COMMON_QUESTIONS[currentTopic] || [];
    
    // Prefetch in the background without blocking the main thread
    const timeoutId = setTimeout(() => {
      questionsToPreload.forEach(async (question) => {
        console.log(`Prefetching: ${question}`);
        
        // Check if we already have this in cache
        const cachedResponse = await getClientResponse(question, currentTopic, language);
        
        // Only prefetch if not already in cache and we're online
        if (!cachedResponse && isOnline) {
          // Create prefetch query key
          const prefetchKey = ['aiResponse', currentTopic, question, language];
          
          // Check if we're already prefetching this
          const existing = queryClient.getQueryData(prefetchKey);
          if (!existing) {
            queryClient.prefetchQuery({
              queryKey: prefetchKey,
              queryFn: async () => {
                // Simulate the request but don't actually send it, just prepare the cache
                console.log(`Prepared cache for: ${question}`);
                return { prepared: true };
              },
              staleTime: 60 * 60 * 1000, // 1 hour
            });
          }
        }
      });
    }, 2000); // Delay prefetching by 2 seconds to avoid blocking the main thread
    
    return () => clearTimeout(timeoutId);
  }, [currentSessionId, currentTopic, isOnline, language, queryClient]);
}
